// src/components/report/manifestTracking/ManifestTrackingReportDashboard.js
import React, { useMemo, useState } from 'react';
import {
    Alert, Box, Button, CircularProgress, LinearProgress, Stack, Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { format } from 'date-fns';

import {
    useManifestTrackingSummary,
    useManifestsTracking,
} from '../../hooks/report/useManifestTrackingReport';

import ManifestTrackingFilters from './manifestTracking/ManifestTrackingFilters';
import ManifestTrackingKpiGrid from './manifestTracking/ManifestTrackingKpiGrid';
import ManifestTrackingTable from './manifestTracking/ManifestTrackingTable';
import ManifestTrackingDetailDialog from './manifestTracking/ManifestTrackingDetailDialog';

const getDefaultFilters = () => {
    const year = new Date().getFullYear();
    return {
        search: '',
        dateFrom: `${year}-01-01`,
        dateTo: format(new Date(), 'yyyy-MM-dd'),
        manEst: '',
        vehID: '',
        empID: '',
        serProID: '',
    };
};

const ManifestTrackingReportDashboard = () => {
    const [filters, setFilters] = useState(getDefaultFilters());
    const [selectedManID, setSelectedManID] = useState(null);

    const summaryQuery = useManifestTrackingSummary(filters);
    const listQuery = useManifestsTracking(filters);

    const isLoading = summaryQuery.isLoading || listQuery.isLoading;
    const isFetching = summaryQuery.isFetching || listQuery.isFetching;

    const summary = summaryQuery.data || {};
    const pages = listQuery.data?.pages || [];
    const rows = useMemo(() => pages.flatMap((p) => p.rows || []), [pages]);
    const total = pages[0]?.total || 0;
    const hasMore = Boolean(listQuery.hasNextPage);

    const handleApplyFilters = (next) => setFilters(next);
    const handleResetFilters = () => setFilters(getDefaultFilters());

    if (isLoading) {
        return (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={3}>
                <Box>
                    <Typography variant="h4" fontWeight={900}>
                        Reporte de tracking de manifiestos
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Checks de vehículo, turnos de conductores/asistentes, kilómetros,
                        rutas y sesiones de tracking.
                    </Typography>
                </Box>

                <ManifestTrackingFilters
                    filters={filters}
                    onApply={handleApplyFilters}
                    onReset={handleResetFilters}
                    isFetching={isFetching}
                />

                {(summaryQuery.isError || listQuery.isError) && (
                    <Alert severity="error">No se pudo cargar el reporte de tracking.</Alert>
                )}

                {isFetching && !listQuery.isFetchingNextPage && (
                    <Alert severity="info">Actualizando información…</Alert>
                )}

                <ManifestTrackingKpiGrid summary={summary} />

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                        Mostrando <strong>{rows.length}</strong> de <strong>{total}</strong> manifiestos
                    </Typography>
                </Stack>

                <ManifestTrackingTable
                    rows={rows}
                    onOpenDetail={(manID) => setSelectedManID(manID)}
                />

                {listQuery.isFetchingNextPage && (
                    <Box sx={{ width: '100%' }}>
                        <LinearProgress />
                    </Box>
                )}

                {hasMore && (
                    <Box display="flex" justifyContent="center" pt={1}>
                        <Button
                            variant="outlined"
                            startIcon={<ExpandMoreIcon />}
                            onClick={() => listQuery.fetchNextPage()}
                            disabled={listQuery.isFetchingNextPage}
                        >
                            {listQuery.isFetchingNextPage ? 'Cargando…' : 'Cargar más'}
                        </Button>
                    </Box>
                )}

                {!hasMore && rows.length > 0 && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        textAlign="center"
                        display="block"
                    >
                        — Fin del listado —
                    </Typography>
                )}
            </Stack>

            <ManifestTrackingDetailDialog
                key={selectedManID || 'none'}     // 👈 fuerza remontar
                open={Boolean(selectedManID)}
                onClose={() => setSelectedManID(null)}
                manID={selectedManID}
            />
        </Box>
    );
};

export default ManifestTrackingReportDashboard;